import { useState } from 'react';
import api from '@/api';
import {
  Mail, Loader2, Copy, CheckCircle2, AlertTriangle,
  X, ChevronDown, ChevronRight, Send
} from 'lucide-react';

const escalationColors = {
  initial: { bg: 'bg-blue-950/30', text: 'text-blue-400', border: 'border-blue-900', label: 'Initial Request' },
  reminder: { bg: 'bg-amber-950/30', text: 'text-amber-400', border: 'border-amber-900', label: 'Reminder' },
  escalation: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/30', label: 'Escalation' },
  retainage_hold: { bg: 'bg-red-950/30', text: 'text-red-400', border: 'border-red-900', label: 'Retainage Hold' },
};

function EmailDraft({ draft }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const esc = escalationColors[draft.escalation_level] || escalationColors.initial;

  const handleCopy = () => {
    navigator.clipboard.writeText(`Subject: ${draft.email_subject}\n\n${draft.email_body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`border rounded-sm ${esc.border} ${esc.bg}`} data-testid={`chase-draft-${draft.responsible_party}`}>
      <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <Mail className={`w-5 h-5 ${esc.text} shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{draft.responsible_party}</p>
          <p className="text-xs text-muted-foreground truncate">{draft.gap_count} outstanding items</p>
        </div>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${esc.bg} ${esc.text} border ${esc.border}`}>
          {esc.label}
        </span>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50">
          <div className="bg-card border border-border rounded-sm p-4 mb-3">
            <p className="text-xs text-muted-foreground mb-1">Subject:</p>
            <p className="text-sm text-foreground font-medium mb-3">{draft.email_subject}</p>
            <p className="text-xs text-muted-foreground mb-1">Body:</p>
            <pre className="text-xs text-foreground/80 whitespace-pre-wrap font-sans leading-relaxed">{draft.email_body}</pre>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleCopy} data-testid="copy-email-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider bg-secondary hover:bg-slate-700 text-foreground/80 rounded-sm transition-colors">
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Copy Email'}
            </button>
          </div>

          {draft.gaps.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Outstanding Items:</p>
              {draft.gaps.map((g, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                  <span>{g.csi_number} {g.trade_name} — Folder {g.folder_letter} ({g.folder_name})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChaseEmailPanel({ projectId, onClose }) {
  const [drafts, setDrafts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/closeout/projects/${projectId}/generate-chase-emails`);
      setDrafts(res.data);
      setGenerated(true);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="chase-email-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-[650px] bg-card border-l border-border overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" /> Chase Email Drafts
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              AI-generated correspondence per SOP cadence (Day 7 → 14 → 21 → retainage hold)
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground/80"><X className="w-5 h-5" /></button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {!generated ? (
            <div className="text-center py-16">
              <Mail className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-2">Generate chase emails from your gap report</p>
              <p className="text-xs text-muted-foreground mb-6">AI will group outstanding items by responsible party and draft professional chase correspondence</p>
              <button onClick={handleGenerate} disabled={loading} data-testid="generate-chase-btn"
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-heading uppercase tracking-wider rounded-sm transition-colors mx-auto">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Generate Chase Emails
              </button>
            </div>
          ) : drafts?.total_gaps === 0 ? (
            <div className="text-center py-16">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <p className="text-lg font-heading text-emerald-400">All items received!</p>
              <p className="text-sm text-muted-foreground mt-2">No chase emails needed — closeout package is complete</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-muted-foreground">
                  {drafts.drafts?.length || 0} parties — {drafts.total_gaps} outstanding items
                </p>
                <button onClick={handleGenerate} disabled={loading}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  {loading ? 'Regenerating...' : 'Regenerate'}
                </button>
              </div>
              <div className="space-y-3">
                {(drafts.drafts || []).map((d, i) => <EmailDraft key={i} draft={d} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
